/**
 * SEO Helper Functions
 * Utility functions for SEO optimization
 */

export interface SEOMetaData {
  title: string;
  description: string;
  keywords: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
}

/**
 * SEO-optimierte Keywords pro Sprache
 */
export const seoKeywords = {
  de: [
    'tägliche motivation app',
    'zitate app iOS',
    'inspirierende sprüche deutsch',
    'motivations app',
    'tägliche inspiration',
    'persönliche entwicklung app',
    'achtsamkeit app',
    'selbstverbesserung app',
    'motivierende zitate',
    'lebensweisheiten app'
  ],
  en: [
    'daily motivation app',
    'inspirational quotes iOS',
    'personal development app',
    'motivation app',
    'daily inspiration',
    'mindfulness app',
    'self improvement app',
    'motivational quotes',
    'life quotes app',
    'daily wisdom'
  ],
  es: [
    'aplicación de motivación diaria',
    'frases inspiradoras iOS',
    'aplicación de desarrollo personal',
    'aplicación de motivación',
    'inspiración diaria',
    'aplicación de mindfulness',
    'aplicación de superación personal',
    'frases motivadoras',
    'citas de vida'
  ],
  fr: [
    'application de motivation quotidienne',
    'citations inspirantes iOS',
    'application de développement personnel',
    'application de motivation',
    'inspiration quotidienne',
    'application de pleine conscience',
    'application d\'amélioration personnelle',
    'citations motivantes',
    'citations de vie'
  ]
};

/**
 * Meta-Daten pro Sprache generieren
 */
export const getMetaDataForLanguage = (language: 'de' | 'en' | 'es' | 'fr'): SEOMetaData => {
  const metaData = {
    de: {
      title: 'Mirolumo - Tägliche Motivation & Inspirierende Zitate App für iOS',
      description: 'Entdecke tägliche Motivation mit Mirolumo. KI-generierte Bilder, 5 inspirierende Kategorien und persönliches Journaling. Deine App für Achtsamkeit und persönliche Entwicklung.',
      keywords: seoKeywords.de.join(', '),
      ogTitle: 'Mirolumo - Tägliche Inspiration & Motivation',
      ogDescription: 'Eine wunderschöne App für tägliche Inspiration mit KI-generierten Bildern, 5 Kategorien und personalem Journaling.',
      twitterTitle: 'Mirolumo - Deine tägliche Motivations-App',
      twitterDescription: 'Tägliche Inspiration, motivierende Zitate und persönliches Journaling in einer eleganten iOS App.'
    },
    en: {
      title: 'Mirolumo - Daily Motivation & Inspirational Quotes App for iOS',
      description: 'Discover daily motivation with Mirolumo. AI-generated images, 5 inspiring categories, and personal journaling. Your app for mindfulness and personal growth.',
      keywords: seoKeywords.en.join(', '),
      ogTitle: 'Mirolumo - Daily Inspiration & Motivation',
      ogDescription: 'A beautiful app for daily inspiration with AI-generated images, 5 categories, and personal journaling.',
      twitterTitle: 'Mirolumo - Your Daily Motivation App',
      twitterDescription: 'Daily inspiration, motivational quotes, and personal journaling in an elegant iOS app.'
    },
    es: {
      title: 'Mirolumo - Motivación Diaria y Frases Inspiradoras App para iOS',
      description: 'Descubre la motivación diaria con Mirolumo. Imágenes generadas por IA, 5 categorías inspiradoras y diario personal. Tu app para mindfulness y crecimiento personal.',
      keywords: seoKeywords.es.join(', '),
      ogTitle: 'Mirolumo - Inspiración y Motivación Diaria',
      ogDescription: 'Una hermosa aplicación para inspiración diaria con imágenes generadas por IA, 5 categorías y diario personal.',
      twitterTitle: 'Mirolumo - Tu App de Motivación Diaria',
      twitterDescription: 'Inspiración diaria, frases motivadoras y diario personal en una elegante app iOS.'
    },
    fr: {
      title: 'Mirolumo - Motivation Quotidienne et Citations Inspirantes App pour iOS',
      description: 'Découvrez la motivation quotidienne avec Mirolumo. Images générées par IA, 5 catégories inspirantes et journal personnel. Votre app pour la pleine conscience et le développement personnel.',
      keywords: seoKeywords.fr.join(', '),
      ogTitle: 'Mirolumo - Inspiration et Motivation Quotidienne',
      ogDescription: 'Une belle application pour l\'inspiration quotidienne avec des images générées par IA, 5 catégories et un journal personnel.',
      twitterTitle: 'Mirolumo - Votre App de Motivation Quotidienne',
      twitterDescription: 'Inspiration quotidienne, citations motivantes et journal personnel dans une élégante app iOS.'
    }
  };

  return {
    ...metaData[language],
    ogImage: 'https://mirolumo.com/og-image.jpg',
    twitterCard: 'summary_large_image',
    twitterImage: 'https://mirolumo.com/og-image.jpg',
    canonical: `https://mirolumo.com${language === 'de' ? '' : `/${language}`}`
  };
};

/**
 * hreflang Links generieren
 */
export const getHreflangLinks = () => {
  return [
    { rel: 'alternate', hreflang: 'de', href: 'https://mirolumo.com/' },
    { rel: 'alternate', hreflang: 'en', href: 'https://mirolumo.com/en' },
    { rel: 'alternate', hreflang: 'es', href: 'https://mirolumo.com/es' },
    { rel: 'alternate', hreflang: 'fr', href: 'https://mirolumo.com/fr' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://mirolumo.com/' }
  ];
};

/**
 * Canonical URL generieren
 */
export const getCanonicalUrl = (language: 'de' | 'en' | 'es' | 'fr'): string => {
  const baseUrl = 'https://mirolumo.com';
  return language === 'de' ? baseUrl : `${baseUrl}/${language}`;
};

/**
 * Breadcrumb JSON-LD generieren
 */
export const getBreadcrumbSchema = (language: 'de' | 'en' | 'es' | 'fr') => {
  const labels = {
    de: 'Startseite',
    en: 'Home',
    es: 'Inicio',
    fr: 'Accueil'
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: labels[language],
        item: getCanonicalUrl(language)
      }
    ]
  };
};


